import GraphQLAPI, {
    GraphQLResult
} from "@aws-amplify/api-graphql";
import {
    AssessmentSchedule,
    CreateAssessmentScheduleInput,
    CreateAssessmentScheduleMutation,
    CreateAssessmentScheduleMutationVariables,
    DeleteAssessmentScheduleMutationVariables,
    GetAssessmentScheduleQuery,
    GetAssessmentScheduleQueryVariables,
    ListAssessmentSchedulesByPropertyQuery,
    UpdateAssessmentScheduleMutation,
    UpdateAssessmentScheduleMutationVariables
} from "../../../API";
import {
    createAssessmentSchedule,
    deleteAssessmentSchedule,
    updateAssessmentSchedule
} from "../../../graphql/mutations";
import {
    getAssessmentSchedule,
    listAssessmentSchedulesByProperty
} from "../../../graphql/queries";

import { AuthIDTokenSupplierFactory } from "../../auth/AuthIDTokenSupplierFactory";
import { IDTokenSupplier } from "../../auth/IDTokenSupplier";
import ClientLogger from "../../logging/ClientLogger";
import ClientLoggerFactory from "../../logging/ClientLoggerFactory";
import { AssessmentScheduleDAO } from "./AssessmentScheduleDAO";
import { ListResponse } from "../../util/data/list/ListResponse";

export class GraphQLAssessmentScheduleDAO implements AssessmentScheduleDAO {
    private static readonly GET_ATTEMPT_METRIC_NAME = "GraphQLAssessmentScheduleDAO.Get.Attempt";
    private static readonly GET_FAILURE_METRIC_NAME = "GraphQLAssessmentScheduleDAO.Get.Failure";
    private static readonly CREATE_ATTEMPT_METRIC_NAME = "GraphQLAssessmentScheduleDAO.Create.Attempt";
    private static readonly CREATE_FAILURE_METRIC_NAME = "GraphQLAssessmentScheduleDAO.Create.Failure";
    private static readonly UPDATE_ATTEMPT_METRIC_NAME = "GraphQLAssessmentScheduleDAO.Update.Attempt";
    private static readonly UPDATE_FAILURE_METRIC_NAME = "GraphQLAssessmentScheduleDAO.Update.Failure";
    private static readonly DELETE_ATTEMPT_METRIC_NAME = "GraphQLAssessmentScheduleDAO.Delete.Attempt";
    private static readonly DELETE_FAILURE_METRIC_NAME = "GraphQLAssessmentScheduleDAO.Delete.Failure";
    private static readonly LIST_ATTEMPT_METRIC_NAME = "GraphQLAssessmentScheduleDAO.List.Attempt";
    private static readonly LIST_FAILURE_METRIC_NAME = "GraphQLAssessmentScheduleDAO.List.Failure";

    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async get(id: string): Promise<AssessmentSchedule> {
        this.logger.info(
            `Getting AssessmentSchedule record with id=${id}`,
            { id },
            [GraphQLAssessmentScheduleDAO.GET_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const variables: GetAssessmentScheduleQueryVariables = {
                id: id
            };
            const result: GraphQLResult<GetAssessmentScheduleQuery> = await GraphQLAPI.graphql({
                query: getAssessmentSchedule,
                variables,
                authToken
            }) as GraphQLResult<GetAssessmentScheduleQuery>;
            const assessmentSchedule = result.data?.getAssessmentSchedule;
            if (!assessmentSchedule) {
                throw new Error(`Unable to get AssessmentSchedule record with id=${id}`);
            }
            return assessmentSchedule;
        } catch (error) {
            const errorMessage = `Unable to get AssessmentSchedule record with id=${id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLAssessmentScheduleDAO.GET_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async listByProperty(propertyId: string, nextToken?: string): Promise<ListResponse<AssessmentSchedule>> {
        this.logger.info(
            `Listing AssessmentSchedule records for property with id=${propertyId}`,
            { propertyId },
            [GraphQLAssessmentScheduleDAO.LIST_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<ListAssessmentSchedulesByPropertyQuery> = await GraphQLAPI.graphql({
                query: listAssessmentSchedulesByProperty,
                variables: { propertyId, nextToken },
                authToken
            }) as GraphQLResult<ListAssessmentSchedulesByPropertyQuery>;
            const assessmentSchedules = result.data?.listAssessmentSchedulesByProperty?.items ?? [];
            return {
                items: assessmentSchedules.filter(assessmentSchedule => !!assessmentSchedule) as Array<AssessmentSchedule>,
                nextToken: result.data?.listAssessmentSchedulesByProperty?.nextToken ?? null
            };
        } catch (error) {
            const errorMessage = `Unable to list AssessmentSchedule records for property with id=${propertyId}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLAssessmentScheduleDAO.LIST_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async create(assessmentSchedule: CreateAssessmentScheduleInput): Promise<AssessmentSchedule> {
        this.logger.info(
            `Creating AssessmentSchedule record`,
            { assessmentSchedule },
            [GraphQLAssessmentScheduleDAO.CREATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<CreateAssessmentScheduleMutation> = await GraphQLAPI.graphql({
                query: createAssessmentSchedule,
                variables: { input: assessmentSchedule } satisfies CreateAssessmentScheduleMutationVariables,
                authToken
            }) as GraphQLResult<CreateAssessmentScheduleMutation>;
            const createdAssessmentSchedule = result.data?.createAssessmentSchedule;
            if (!createdAssessmentSchedule) {
                throw new Error(`Unable to create AssessmentSchedule record`);
            }
            return createdAssessmentSchedule;
        } catch (error) {
            const errorMessage = `Unable to create AssessmentSchedule record`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLAssessmentScheduleDAO.CREATE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async update(assessmentSchedule: AssessmentSchedule): Promise<AssessmentSchedule> {
        this.logger.info(
            `Updating AssessmentSchedule record with id=${assessmentSchedule.id}`,
            { assessmentSchedule },
            [GraphQLAssessmentScheduleDAO.UPDATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<UpdateAssessmentScheduleMutation> = await GraphQLAPI.graphql({
                query: updateAssessmentSchedule,
                /**
                 * Input is possibly of type API AssessmentSchedule,
                 * which contains unexpected fields: _typename, _lastChangedAt, _version
                 * Need to cast it to the expected type to satisfy the type check. 
                 */
                variables: {
                    input: {
                        id: assessmentSchedule.id,
                        _version: assessmentSchedule._version,
                        name: assessmentSchedule.name,
                        description: assessmentSchedule.description ?? null,
                        startDate: assessmentSchedule.startDate,
                        endDate: assessmentSchedule.endDate ?? null,
                        authorizedIdentifiers: assessmentSchedule.authorizedIdentifiers ?? null,
                        recurrence: {
                            type: assessmentSchedule.recurrence?.type ?? null,
                            dailyRecurrence: assessmentSchedule.recurrence?.dailyRecurrence ? {
                                interval: assessmentSchedule.recurrence.dailyRecurrence.interval
                            } : null,
                            weeklyRecurrence: assessmentSchedule.recurrence?.weeklyRecurrence ? {
                                interval: assessmentSchedule.recurrence.weeklyRecurrence.interval,
                                daysOfWeek: assessmentSchedule.recurrence.weeklyRecurrence.daysOfWeek
                            } : null,
                            monthlyRecurrence: assessmentSchedule.recurrence?.monthlyRecurrence ? {
                                interval: assessmentSchedule.recurrence.monthlyRecurrence.interval,
                                type: assessmentSchedule.recurrence.monthlyRecurrence.type,
                                dayOfMonth: assessmentSchedule.recurrence.monthlyRecurrence.dayOfMonth ?? null,
                                nthWeekDay: assessmentSchedule.recurrence.monthlyRecurrence.nthWeekDay ? {
                                    weekNumber: assessmentSchedule.recurrence.monthlyRecurrence.nthWeekDay.weekNumber,
                                    day: assessmentSchedule.recurrence.monthlyRecurrence.nthWeekDay.day
                                } : null
                            } : null
                        }
                    }
                } satisfies UpdateAssessmentScheduleMutationVariables,
                authToken
            }) as GraphQLResult<UpdateAssessmentScheduleMutation>;
            const updatedAssessmentSchedule = result.data?.updateAssessmentSchedule;
            if (!updatedAssessmentSchedule) {
                throw new Error(`Unable to update AssessmentSchedule record with id=${assessmentSchedule.id}`);
            }
            return updatedAssessmentSchedule;
        } catch (error) {
            const errorMessage = `Unable to update AssessmentSchedule record with id=${assessmentSchedule.id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLAssessmentScheduleDAO.UPDATE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }

    public async delete(
        id: string,
        version: number
    ): Promise<void> {
        this.logger.info(
            `Deleting AssessmentSchedule record with id=${id}`,
            { id },
            [GraphQLAssessmentScheduleDAO.DELETE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            await GraphQLAPI.graphql({
                query: deleteAssessmentSchedule,
                variables: {
                    input: {
                        id,
                        _version: version
                    }
                } satisfies DeleteAssessmentScheduleMutationVariables,
                authToken
            });
        } catch (error) {
            const errorMessage = `Unable to delete AssessmentSchedule record with id=${id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLAssessmentScheduleDAO.DELETE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage, { cause: error });
        }
    }
}

export const graphQLAssessmentScheduleDAO: AssessmentScheduleDAO = new GraphQLAssessmentScheduleDAO(
    ClientLoggerFactory.getClientLogger("GraphQLAssessmentScheduleDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);